import type { ReducceEvent } from "@/features/boutique/types/event.type";
import type { Article, Ref } from "@/features/boutique/types/ref.type";

export const getEventNumericId = (eventId: string): number => {
  const parsed = Number.parseInt(eventId, 10);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }
  return eventId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
};

const mapEventSessionsToArticles = (event: ReducceEvent): Article[] => {
  const basePrice = event.min_price_amount ?? "0";
  const numericId = getEventNumericId(event.id);

  if (!event.sessions?.length) {
    return [{
      id: numericId,
      name: event.name,
      kind: "event",
      original_price_amount: basePrice,
      discount_amount: "0",
      price_amount: basePrice,
      minimum_quantity: 1,
      multiple_quantity: 1,
      excerpt: null,
      description: null,
      details: null,
      quantities: [1],
    }];
  }

  return event.sessions.map((session, index) => ({
    id: (numericId * 1000) + index,
    name: (session.date || session.session_date)
      ? `${event.name} - ${session.date || session.session_date}`
      : event.name,
    kind: "event_session",
    original_price_amount: basePrice,
    discount_amount: "0",
    price_amount: basePrice,
    minimum_quantity: 1,
    multiple_quantity: 1,
    excerpt: session.code || session.session_code || null,
    description: null,
    details: null,
    quantities: [1],
  }));
};

export const mapEventToRef = (event: ReducceEvent): Ref => {
  const numericId = getEventNumericId(event.id);
  const location = event.localization;

  return {
    id: numericId,
    event_id: event.id,
    name: event.name,
    image_url: event.image_url,
    discount_percentage: 0,
    articles: mapEventSessionsToArticles(event),
    excerpt: event.excerpt ?? undefined,
    description: event.description ?? undefined,
    dynamic_prices: event.dynamic_prices,
    localization: location
      ? {
        department_id: 0,
        department_name: location.city || location.name || "",
        region_name: location.region || "",
        latitude: location.latitude ?? null,
        longitude: location.longitude ?? null,
        name: location.name,
        address1: location.address1,
        address2: location.address2,
        postal_code: location.postal_code,
        city: location.city,
        region: location.region,
        country: location.country,
      }
      : null,
    sessions: event.sessions?.map((session) => ({
      session_code: session.session_code ?? session.code ?? "",
      session_date: session.session_date ?? session.date ?? "",
    })),
    category_name: event.category_name,
    subcategory_name: event.subcategory_name,
    min_price_amount: event.min_price_amount,
    status: event.status,
    maximum_quantity: event.maximum_quantity,
    conditions_tarifaires: event.conditions_tarifaires,
    placement: event.placement,
    plan: event.plan ?? null,
    type_billet: event.type_billet,
    type_billet_delivery_mode: event.type_billet_delivery_mode,
    has_seat_selection: event.has_seat_selection,
  };
};
